import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Button, Typography, Tooltip } from '@mui/material';
import Grid from '@mui/material/Grid2';
import dayjs from 'dayjs';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AppAlert from '@ui/components/common/Alert';
import ControlledHCheckboxAutocomplete from '@ui/components/common/Autocomplete/ControlledHCheckboxAutocomplete';
import { HDatePicker } from '@ui/components/common/Pickers';
import { EmailTemplateType } from '@application/features/emailTemplates/models/email-template-type';
import { updateEmailTemplateSettingsAsync, getEmailTemplateSettingsAsync } from '@application/features/emailTemplateSettings/emailTemplateSettingsThunk';
import { resetEmailTemplateSettingsApiState, clearEmailTemplateSettings } from '@application/features/emailTemplateSettings/emailTemplateSettingsSlice';
import { EmailRecipientGroup, AttendeeFilter } from '@application/features/emailTemplateSettings/models/email-template-settings-recipient-group-record';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { convertUtcEmailTimesToLocal, convertLocalEmailTimesToUtc } from '@application/utilities/date-time-formater';
import SingleSelect from '@ui/components/common/Autocomplete/SingleSelect';
const EmailSettings = ({ templateTitle, templateType, emailTemplateId }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [sendDate, setSendDate] = useState(null);
    const [sendTime, setSendTime] = useState(null);
    const [dateTimeErrors, setDateTimeErrors] = useState({});
    const VDKEventsForEventDetails = useAppSelector((state) => state.VDKEvent.VDKEventsForEventDetails);
    const [sentScheduledEmail, setSentScheduledEmail] = useState(false);
    const isInvitationOrReInvitation = templateType === EmailTemplateType.Invitation ||
        templateType === EmailTemplateType.ReInvitationReminder;
    const mustBeBeforeEventStart = templateType === EmailTemplateType.Invitation ||
        templateType === EmailTemplateType.ReInvitationReminder ||
        templateType === EmailTemplateType.RegisteredReminder ||
        templateType === EmailTemplateType.FinalDetails;
    const mustBeAfterEventEnd = templateType === EmailTemplateType.PostEventThankYou;
    const hasScheduleDateGuidance = mustBeBeforeEventStart || mustBeAfterEventEnd;
    const isThankYouEmail = templateType === EmailTemplateType.WelcomeThankYou ||
        templateType === EmailTemplateType.Ticket ||
        templateType === EmailTemplateType.RegisteredReminder ||
        templateType === EmailTemplateType.FinalDetails ||
        templateType === EmailTemplateType.PostEventThankYou;
    const sendToOptions = useMemo(() => {
        const options = [];
        if (isInvitationOrReInvitation) {
            options.push({
                label: t('events.eventDetails.preferencesTab.notRegisteredAttendees'),
                value: String(EmailRecipientGroup.AttendeesNotRegistered)
            });
        }
        else if (isThankYouEmail) {
            // For non-Invitation emails, add Registered Attendees and RSVP options to sendTo
            options.push({
                label: t('events.eventDetails.preferencesTab.registeredAttendees'),
                value: String(EmailRecipientGroup.AttendeesConfirmed)
            });
            // Only show "Attendees & Guests" if event has AdditionalAttendee field
            if (VDKEventsForEventDetails?.hasAdditionalAttendeeField) {
                options.push({
                    label: t('events.eventDetails.preferencesTab.registeredAttendeesWithFamilyGuest'),
                    value: String(EmailRecipientGroup.AttendeesAndGuests)
                });
            }
        }
        return options.length > 0
            ? options
            : [
                {
                    label: t('events.eventDetails.preferencesTab.registeredAttendees'),
                    value: String(EmailRecipientGroup.AttendeesConfirmed)
                }
            ];
    }, [
        t,
        isInvitationOrReInvitation,
        isThankYouEmail,
        VDKEventsForEventDetails?.hasAdditionalAttendeeField
    ]);
    const attendeesOptions = useMemo(() => {
        // Only show attendee filter options for Invitation/ReInvitation emails
        if (!isInvitationOrReInvitation) {
            return [
                {
                    label: t('events.eventDetails.preferencesTab.allAttendees'),
                    value: String(AttendeeFilter.All),
                    description: t('events.eventDetails.preferencesTab.allAttendeesInfoTooltip')
                }
            ];
        }
        const options = [
            {
                label: t('events.eventDetails.preferencesTab.allAttendees'),
                value: String(AttendeeFilter.All),
                description: t('events.eventDetails.preferencesTab.allAttendeesInfoTooltip')
            },
            {
                label: t('events.eventDetails.preferencesTab.rsvpOnly'),
                value: String(AttendeeFilter.RsvpOnly),
                description: t('events.eventDetails.preferencesTab.rsvpOnlyInfoTooltip')
            }
        ];
        // Only include "Registered Attendees with Family/Guest" if event has AdditionalAttendee field
        if (VDKEventsForEventDetails?.hasAdditionalAttendeeField) {
            options.push({
                label: t('events.eventDetails.preferencesTab.registeredAttendeesWithFamilyGuest'),
                value: String(AttendeeFilter.AttendeesOnly),
                description: t('events.eventDetails.preferencesTab.attendeeOnlyInfoTooltip')
            });
        }
        return options;
    }, [t, isInvitationOrReInvitation, VDKEventsForEventDetails?.hasAdditionalAttendeeField]);
    const { control, getValues, reset, formState } = useForm({
        mode: 'onBlur',
        defaultValues: {
            sendTo: sendToOptions.length > 0 ? sendToOptions[0].value : null,
            attendees: [attendeesOptions[0]],
            resendPeriod: 'never'
        }
    });
    const selectedSendTo = useWatch({
        control,
        name: 'sendTo'
    });
    const filteredAttendeeOptions = useMemo(() => {
        if (!selectedSendTo) {
            return attendeesOptions;
        }
        const isNotRegisteredSelected = selectedSendTo === String(EmailRecipientGroup.AttendeesNotRegistered);
        // If "Not Registered Attendees" is selected, only show "RSVP Only"
        if (isNotRegisteredSelected) {
            return attendeesOptions.filter((opt) => opt.value === String(AttendeeFilter.RsvpOnly));
        }
        const options = attendeesOptions.map((opt) => {
            if (opt.value === String(AttendeeFilter.RsvpOnly)) {
                return {
                    ...opt,
                    label: t('events.eventDetails.preferencesTab.rsvpRegisteredLabel'),
                    description: t('events.eventDetails.preferencesTab.rsvpRegisteredInfoTooltip')
                };
            }
            return opt;
        });
        return options;
    }, [selectedSendTo, attendeesOptions, t]);
    const { emailTemplateSettings } = useAppSelector((state) => state.emailTemplateSettings);
    const resendOptions = [
        {
            label: t('events.eventDetails.preferencesTab.never'),
            value: 'never'
        }
    ];
    useEffect(() => {
        dispatch(clearEmailTemplateSettings());
        dispatch(getEmailTemplateSettingsAsync(emailTemplateId));
    }, [emailTemplateId, dispatch]);
    useEffect(() => {
        if (emailTemplateSettings) {
            const firstGroup = emailTemplateSettings.recipientGroups?.[0];
            const uniqueRecipientGroups = firstGroup
                ? (sendToOptions.find((opt) => opt.value === String(firstGroup.recipientGroup))?.value ?? null)
                : (sendToOptions[0]?.value ?? null);
            const defaultAttendees = emailTemplateSettings.recipientGroups?.length > 0
                ? emailTemplateSettings.recipientGroups
                    .map((rg) => ({
                    label: attendeesOptions.find((opt) => opt.value === String(rg.attendeeFilter))?.label ||
                        '',
                    value: String(rg.attendeeFilter)
                }))
                    .filter((item, index, self) => self.findIndex((t) => t.value === item.value) === index)
                : [];
            reset({
                sendTo: uniqueRecipientGroups,
                attendees: defaultAttendees,
                resendPeriod: 'never'
            });
            // Convert UTC values to local timezone for display
            if (emailTemplateSettings.scheduledDate && emailTemplateSettings.scheduledTime) {
                const { localDate, localTime } = convertUtcEmailTimesToLocal(emailTemplateSettings.scheduledDate, emailTemplateSettings.scheduledTime);
                setSendDate(localDate);
                setSendTime(localTime);
            }
            else {
                setSendDate(null);
                setSendTime(null);
            }
            setSentScheduledEmail(emailTemplateSettings.sentScheduledEmail ?? false);
        }
    }, [emailTemplateSettings, reset, sendToOptions, attendeesOptions]);
    useEffect(() => {
        return () => {
            dispatch(resetEmailTemplateSettingsApiState());
        };
    }, [dispatch]);
    const formatScheduleHintDate = useCallback((dateStr) => {
        if (!dateStr)
            return '';
        return dayjs(dateStr).format('MMM D, YYYY');
    }, []);
    const scheduleDateHint = useMemo(() => {
        if (!hasScheduleDateGuidance)
            return null;
        if (mustBeBeforeEventStart) {
            const eventStartDate = formatScheduleHintDate(VDKEventsForEventDetails?.startDate);
            if (!eventStartDate) {
                return t('events.eventDetails.preferencesTab.scheduleDateHintBeforeEventStartGeneric');
            }
            return t('events.eventDetails.preferencesTab.scheduleDateHintBeforeEventStart', {
                eventStartDate
            });
        }
        if (mustBeAfterEventEnd) {
            const eventEndDate = formatScheduleHintDate(VDKEventsForEventDetails?.endDate);
            if (!eventEndDate) {
                return t('events.eventDetails.preferencesTab.scheduleDateHintAfterEventEndGeneric');
            }
            return t('events.eventDetails.preferencesTab.scheduleDateHintAfterEventEnd', {
                eventEndDate
            });
        }
        return null;
    }, [
        hasScheduleDateGuidance,
        mustBeBeforeEventStart,
        mustBeAfterEventEnd,
        VDKEventsForEventDetails?.startDate,
        VDKEventsForEventDetails?.endDate,
        formatScheduleHintDate,
        t
    ]);
    const validateScheduleDate = () => {
        const errors = {};
        if (sentScheduledEmail) {
            setDateTimeErrors({});
            return true;
        }
        if (!sendDate) {
            setDateTimeErrors({});
            return true;
        }
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const selectedDate = new Date(sendDate);
        selectedDate.setHours(0, 0, 0, 0);
        if (isInvitationOrReInvitation) {
            if (selectedDate < today) {
                errors.dateError =
                    t('events.eventDetails.preferencesTab.dateCannotBeInPast') ||
                        'Date cannot be in the past';
            }
            if (VDKEventsForEventDetails?.startDate) {
                const eventStartDate = new Date(VDKEventsForEventDetails.startDate);
                eventStartDate.setHours(0, 0, 0, 0);
                if (selectedDate > eventStartDate) {
                    errors.dateError =
                        t('events.eventDetails.preferencesTab.dateCannotBeAfterEventStart') ||
                            'Date cannot be after event start date';
                }
            }
        }
        if (Object.keys(errors).length > 0) {
            setDateTimeErrors(errors);
            return false;
        }
        return true;
    };
    const handleSaveChanges = async () => {
        const formValues = getValues();
        if (!formValues.sendTo) {
            return;
        }
        if (!formValues.attendees || formValues.attendees.length === 0) {
            return;
        }
        if (!validateScheduleDate()) {
            return;
        }
        setDateTimeErrors({});
        const attendeeFilterValues = formValues.attendees.map((opt) => parseInt(opt.value));
        const recipientGroups = attendeeFilterValues.map((attendeeFilter) => ({
            recipientGroup: parseInt(formValues.sendTo ? formValues.sendTo : '1'),
            attendeeFilter
        }));
        // Convert local time to UTC before sending to backend
        const { utcDate, utcTime } = convertLocalEmailTimesToUtc(sendDate ?? undefined, sendTime ?? undefined);
        const updatePayload = {
            emailTemplateId,
            recipientGroups
        };
        // Only include date/time if they have values
        if (utcDate) {
            updatePayload.scheduledDate = utcDate;
        }
        if (utcTime) {
            updatePayload.scheduledTime = utcTime;
        }
        await dispatch(updateEmailTemplateSettingsAsync(updatePayload))
            .unwrap()
            .then(async (response) => {
            if (response.isSuccess) {
                toast.success(response.message);
                await dispatch(getEmailTemplateSettingsAsync(emailTemplateId));
            }
            else if (!response.isSuccess) {
                toast.error(response.message);
            }
        })
            .catch((error) => { });
    };
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 2, children: [_jsxs(Box, { children: [_jsx(Typography, { variant: 'h4', color: 'text.primary', children: templateTitle }), _jsx(Typography, { variant: 'body1', color: 'text.secondary', mt: 0.5, children: t('events.eventDetails.preferencesTab.defineWhenEmailSent') })] }), _jsx(Button, { variant: 'contained', color: 'primary', size: 'large', onClick: handleSaveChanges, children: t('events.eventDetails.preferencesTab.saveChanges') })] }), _jsxs(Grid, { container: true, spacing: 1, alignItems: 'center', children: [_jsx(Grid, { size: { xs: 12, md: 3 }, children: _jsx(Typography, { variant: 'body1', color: 'text.primary', children: t('events.eventDetails.preferencesTab.sendTo') }) }), _jsx(Grid, { size: { xs: 12, md: 9 }, children: _jsxs(Grid, { container: true, spacing: 1, children: [_jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(SingleSelect, { name: 'sendTo', label: t('events.eventDetails.preferencesTab.select'), control: control, options: sendToOptions, bg: 'grey8', rules: {
                                            required: t('events.eventDetails.preferencesTab.recipientGroupIsRequired')
                                        }, error: formState.errors.sendTo?.message }) }), isInvitationOrReInvitation && (_jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(ControlledHCheckboxAutocomplete, { name: 'attendees', label: t('events.eventDetails.preferencesTab.attendees'), control: control, options: filteredAttendeeOptions, bg: 'grey8', checkboxMode: true, maxTags: 2, disablingOptionValue: String(AttendeeFilter.All), rules: {
                                            required: t('events.eventDetails.preferencesTab.attendeeFilterIsRequired')
                                        }, error: formState.errors.attendees?.message }) }))] }) }), _jsx(Grid, { size: { xs: 12, md: 3 }, children: _jsx(Typography, { variant: 'body1', color: 'text.primary', children: t('events.eventDetails.preferencesTab.whenShouldEmailBeSent') }) }), _jsx(Grid, { size: { xs: 12, md: 9 }, children: _jsxs(Box, { display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1.5, alignItems: { xs: 'stretch', md: 'baseline' }, children: [_jsx(Box, { width: { xs: '100%', md: 280 }, flexShrink: 0, children: _jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 0.5, children: [_jsx(Tooltip, { title: sentScheduledEmail
                                                    ? t('events.eventDetails.preferencesTab.emailHasBeenSentBySchedule') ||
                                                        'This email has been sent by the scheduler. The date and time are now disabled.'
                                                    : '', disableHoverListener: !sentScheduledEmail, children: _jsx(Box, { sx: {
                                                        opacity: sentScheduledEmail ? 0.6 : 1,
                                                        backgroundColor: sentScheduledEmail ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                                                        borderRadius: 1,
                                                        padding: sentScheduledEmail ? 1 : 0
                                                    }, children: _jsx(HDatePicker, { label: t('events.eventDetails.preferencesTab.selectDate'), value: sendDate ?? '', handleSelectedDate: (date) => {
                                                            setSendDate(date);
                                                            if (!date) {
                                                                setSendTime(null);
                                                                setDateTimeErrors({});
                                                                return;
                                                            }
                                                            setDateTimeErrors((prev) => ({ ...prev, dateError: undefined }));
                                                        }, minDate: new Date(), maxDate: isInvitationOrReInvitation && VDKEventsForEventDetails?.startDate
                                                            ? new Date(VDKEventsForEventDetails.startDate)
                                                            : undefined, readOnly: sentScheduledEmail, bg: 'grey8', clearable: true }) }) }), dateTimeErrors.dateError && (_jsx(Typography, { variant: 'caption', color: 'error', children: dateTimeErrors.dateError }))] }) }), hasScheduleDateGuidance && scheduleDateHint && !sentScheduledEmail && (_jsx(AppAlert, { severity: 'info', sx: {
                                        flex: 1,
                                        paddingTop: '10px',
                                        '& .MuiAlert-message': { padding: '6px 0px' }
                                    }, children: _jsx(Typography, { variant: 'body2', component: 'span', children: scheduleDateHint }) }))] }) }), ' '] })] }));
};
export default EmailSettings;
